/**
 * RelacaoVendasPage Component
 * 
 * Página de listagem e gerenciamento de vendas.
 * Permite visualizar vendas, finalizar pré-vendas aguardando e exportar relatórios.
 * 
 * Funcionalidades:
 * - Listagem de vendas com paginação
 * - Filtros por status, cliente, data, vendedor
 * - Visualização de detalhes
 * - Botão "Aguardando" - mostra pré-vendas confirmadas aguardando finalização
 * - Selecionar pré-venda e finalizar como venda
 * - Exportação de relatórios (futuro)
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Eye, Search, Filter, Calendar, Download, Copy, ExternalLink, Check, History,
  MoreVertical, Loader2, FileText, X, CheckCircle2, Ban, FileEdit, CreditCard
} from 'lucide-react'
import * as XLSX from 'xlsx'
import { CalendarRangePicker } from '../../../../components/ERP/Calendar/Calendar'
import FilterLeftPicker from '../../../../components/ERP/FilterLeftPicker'
import ERPTable from '../../../../components/ERP/ERPTable'
import Modal from '../../../../components/ERP/Modal/Modal'
import ModalERP from '../../../../components/ERP/ModalERP'
import ConfirmModal from '../../../../components/ERP/ConfirmModal/ConfirmModal'
import VendaFiscalDropdownMenu from '../../../../components/ERP/Vendas/VendaFiscalDropdownMenu'
import EmitirNotaFiscalModal from '../../../../components/ERP/Vendas/EmitirNotaFiscalModal'
import EmitirDevolucaoModal from '../../../../components/ERP/Vendas/EmitirDevolucaoModal'
import DanfeViewerModal from '../../../../components/ERP/Vendas/DanfeViewerModal'
import NfceViewerModal from '../../../../components/ERP/Vendas/NfceViewerModal'
import NFeCancelModal from '../../../../components/ERP/NFe/NFeCancelModal'
import NFeCorrigirRazaoSocialModal, { RAZAO_SOCIAL_CANCEL_JUSTIFICATION } from '../../../../components/ERP/NFe/NFeCorrigirRazaoSocialModal'
import ModalAutorizacaoCancelamento from '../../../../components/ERP/ModalAutorizacaoCancelamento/ModalAutorizacaoCancelamento'
import { Toast } from '../../../../components'
import { useToast } from '../../../../hooks/useToast'
import {
  saleService,
  customerService,
  salePaymentService,
  nfeService,
  saleReturnService,
  paymentReceiptMethodService
} from '../../../../services/erpApi'
import { fiscalService } from '../../../../services/api'
import { getDocumentoFiscalLabel, getVendaDocumentoFiscal, VENDA_DOC_TIPO, getDevolucaoBadge } from '../../../../utils/vendaFiscalDocument'
import { usePermissions } from '../../../../hooks/usePermissions'
import { MODULES, ACTIONS } from '../../../../utils/permissions'
import { isChaveNfce } from '../../../../utils/fiscalChaveUtils'
import { formatCPF } from '../../../../utils/cpfUtils'
import '../PreVendas/PreVendasPage.css'
import '../NFe/NFeListPage.css'
import './RelacaoVendasPage.css'

const STATUS_OPTIONS = [
  { id: 'draft', label: 'Rascunho', icon: FileEdit },
  { id: 'confirmed', label: 'Confirmada', icon: CheckCircle2 },
  { id: 'completed', label: 'Concluída', icon: Check },
  { id: 'cancelled', label: 'Cancelada', icon: Ban }
]

const EMPTY_FILTERS = {
  search: '',
  status: '',
  customerId: '',
  dateFrom: '',
  dateTo: '',
  authorizationNumber: '',
  paymentReceiptMethodId: ''
}

function RelacaoVendasPage() {
  const navigate = useNavigate()
  const { toast, showToast, hideToast } = useToast()
  const [vendas, setVendas] = useState([])
  const [loading, setLoading] = useState(true)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [selectedVenda, setSelectedVenda] = useState(null)
  const [clientes, setClientes] = useState([])
  const [copiedAuthNumber, setCopiedAuthNumber] = useState(null) // Para feedback de cópia
  const [authorizationHistory, setAuthorizationHistory] = useState({}) // { paymentId: history[] }
  const [loadingHistory, setLoadingHistory] = useState({}) // { paymentId: boolean }
  const [historyModalOpen, setHistoryModalOpen] = useState(false)
  const [selectedPaymentForHistory, setSelectedPaymentForHistory] = useState(null)
  const [extornosPendentes, setExtornosPendentes] = useState({}) // { saleId: { tem_extornos_pendentes, extornos_pendentes } }
  const [openDropdownId, setOpenDropdownId] = useState(null)
  const [menuVenda, setMenuVenda] = useState(null)
  const menuAnchorRef = useRef(null)
  const [cancelNfeModalOpen, setCancelNfeModalOpen] = useState(false)
  const [cancelNfeInitialJustification, setCancelNfeInitialJustification] = useState('')
  const [corrigirRazaoModalOpen, setCorrigirRazaoModalOpen] = useState(false)
  const [savingRazaoSocial, setSavingRazaoSocial] = useState(false)
  const [cancelNfceModalOpen, setCancelNfceModalOpen] = useState(false)
  const [selectedVendaFiscal, setSelectedVendaFiscal] = useState(null)
  const [cancellingNfe, setCancellingNfe] = useState(false)
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null })
  const [danfeModalOpen, setDanfeModalOpen] = useState(false)
  const [danfeUrl, setDanfeUrl] = useState(null)
  const [danfeTitle, setDanfeTitle] = useState(null)
  const [nfceModalOpen, setNfceModalOpen] = useState(false)
  const [vendaParaVisualizarNfce, setVendaParaVisualizarNfce] = useState(null)
  const [cupomModalOpen, setCupomModalOpen] = useState(false)
  const [cupomTexto, setCupomTexto] = useState('')
  const [cupomLoading, setCupomLoading] = useState(false)
  const [emitirNotaModalOpen, setEmitirNotaModalOpen] = useState(false)
  const [vendaParaEmitirNota, setVendaParaEmitirNota] = useState(null)
  const [emitirDevolucaoModalOpen, setEmitirDevolucaoModalOpen] = useState(false)
  const [vendaParaDevolucao, setVendaParaDevolucao] = useState(null)
  const { hasPermission } = usePermissions()
  const canReturnNfe = hasPermission(MODULES.SALES, ACTIONS.SALES_RETURN_NFE)
  const periodBtnRef = useRef(null)
  const [paymentMethods, setPaymentMethods] = useState([])
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS })
  const [draftFilters, setDraftFilters] = useState(null)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isPeriodOpen, setIsPeriodOpen] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })

  const paymentMethodOptions = useMemo(
    () =>
      (paymentMethods || [])
        .map((method) => ({
          value: String(method.id),
          label: method.name || method.payment_method_name || `Método #${method.id}`
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
    [paymentMethods]
  )

  const customerFilterOptions = useMemo(
    () =>
      (clientes || []).map((cliente) => ({
        value: String(cliente.id),
        label: cliente.name
      })),
    [clientes]
  )

  const hasDateRangeFilter = Boolean(filters.dateFrom || filters.dateTo)

  const hasActiveFilters = useMemo(() => {
    return Boolean(
      filters.status ||
      filters.customerId ||
      filters.authorizationNumber
    )
  }, [filters.status, filters.customerId, filters.authorizationNumber])

  const enrichVendasWithNfe = async (sales) => {
    if (!sales?.length) return []

    return Promise.all(
      sales.map(async (sale) => {
        const tipoLista = (sale.fiscal_document_type || '').toLowerCase()

        if (tipoLista === 'nfe' || sale.nfe_id) {
          return {
            ...sale,
            fiscal_document_type: 'nfe',
            nfe_id: sale.nfe_id
          }
        }

        try {
          const nfeRes = await nfeService.getAll({ saleId: sale.id, limit: 1 })
          const nfe = nfeRes.success && nfeRes.data?.length ? nfeRes.data[0] : null
          if (nfe) {
            return {
              ...sale,
              fiscal_document_type: 'nfe',
              nfe_id: nfe.id,
              nfe_status: nfe.status,
              nfe_number: nfe.number,
              nfe_access_key: nfe.access_key,
              nfe_url_pdf: nfe.url_pdf
            }
          }
        } catch (err) {
          console.warn(`Não foi possível buscar NF-e da venda ${sale.id}:`, err)
        }

        if (tipoLista === 'nfce' || isChaveNfce(sale.chave_nfe)) {
          return { ...sale, fiscal_document_type: 'nfce' }
        }

        if (tipoLista === 'cupom') {
          return { ...sale, fiscal_document_type: 'cupom' }
        }

        return { ...sale, fiscal_document_type: 'cupom' }
      })
    )
  }

  const loadVendas = useCallback(async () => {
    setLoading(true)
    try {
      const params = {
        search: filters.search || undefined,
        status: filters.status || undefined,
        customerId: filters.customerId || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        authorizationNumber: filters.authorizationNumber || undefined,
        paymentReceiptMethodId: filters.paymentReceiptMethodId || undefined,
        type: 'sale',
        page: pagination.page,
        limit: pagination.limit
      }

      const response = await saleService.getAll(params)
      
      if (response.success) {
        const enriched = await enrichVendasWithNfe(response.data || [])
        setVendas(enriched)
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          }))
        }
      }
    } catch (error) {
      console.error('Erro ao carregar vendas:', error)
    } finally {
      setLoading(false)
    }
  }, [filters, pagination.page, pagination.limit])

  useEffect(() => {
    loadVendas()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadVendas])

  useEffect(() => {
    loadClientes()
    loadPaymentMethods()
  }, [])

  const loadPaymentMethods = async () => {
    try {
      const response = await paymentReceiptMethodService.getForSales({ active: true })
      if (response.success) {
        setPaymentMethods(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar métodos de pagamento:', error)
    }
  }

  const handlePeriodRangeChange = (start, end) => {
    let from = start
    let to = end
    if (from && to && from > to) {
      ;[from, to] = [to, from]
    }
    setFilters((prev) => ({
      ...prev,
      dateFrom: from || '',
      dateTo: to || ''
    }))
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handleClearPeriodFilter = () => {
    setFilters((prev) => ({
      ...prev,
      dateFrom: '',
      dateTo: ''
    }))
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handlePaymentMethodChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      paymentReceiptMethodId: value || ''
    }))
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const openFilterSidebar = () => {
    setDraftFilters({
      status: filters.status,
      customerId: filters.customerId,
      authorizationNumber: filters.authorizationNumber
    })
    setIsFilterOpen(true)
  }

  const applySidebarFilters = () => {
    if (!draftFilters) return
    setFilters((prev) => ({
      ...prev,
      status: draftFilters.status || '',
      customerId: draftFilters.customerId || '',
      authorizationNumber: draftFilters.authorizationNumber || ''
    }))
    setPagination((prev) => ({ ...prev, page: 1 }))
    setIsFilterOpen(false)
    setDraftFilters(null)
  }

  const resetFilters = () => {
    setFilters({ ...EMPTY_FILTERS })
    setDraftFilters(null)
    setIsFilterOpen(false)
    setIsPeriodOpen(false)
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest('.nfe-dropdown-menu-fixed')) return
      if (event.target.closest('.venda-fiscal-dropdown-menu')) return
      if (openDropdownId != null && !event.target.closest('.dropdown-wrapper')) {
        setOpenDropdownId(null)
        setMenuVenda(null)
        menuAnchorRef.current = null
      }
    }

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openDropdownId])

  // Verificar extornos pendentes para vendas canceladas
  useEffect(() => {
    const verificarExtornos = async () => {
      const vendasCanceladas = vendas.filter(v => v.status === 'cancelled')
      if (vendasCanceladas.length === 0) return

      const extornos = {}
      for (const venda of vendasCanceladas) {
        try {
          const response = await saleService.verificarExtornosPendentes(venda.id)
          if (response.success) {
            extornos[venda.id] = response.data
          }
        } catch (error) {
          console.error(`Erro ao verificar extornos para venda ${venda.id}:`, error)
        }
      }
      setExtornosPendentes(extornos)
    }

    if (vendas.length > 0) {
      verificarExtornos()
    }
  }, [vendas])

  // Buscar todas as vendas para exportação (sem paginação)
  const loadAllVendasForExport = async () => {
    try {
      const response = await saleService.getAll({
        search: filters.search || undefined,
        status: filters.status || undefined,
        customerId: filters.customerId || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        authorizationNumber: filters.authorizationNumber || undefined,
        paymentReceiptMethodId: filters.paymentReceiptMethodId || undefined,
        type: 'sale',
        limit: 10000,
        page: 1
      })
      
      if (response.success) {
        return response.data || []
      }
      return []
    } catch (error) {
      console.error('Erro ao carregar vendas para exportação:', error)
      return []
    }
  }

  const loadClientes = async () => {
    try {
      const response = await customerService.getAll({ status: 'active' })
      if (response.success) {
        setClientes(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error)
    }
  }

  const handleViewDetails = async (venda) => {
    try {
      const response = await saleService.getById(venda.id)
      if (response.success) {
        setSelectedVenda(response.data)
        setDetailModalOpen(true)
        
        // Carregar histórico de autorização para todos os pagamentos
        if (response.data.payments) {
          response.data.payments.forEach(async (payment) => {
            if (payment.id) {
              await loadAuthorizationHistory(payment.id)
            }
          })
        }
      }
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error)
      alert('Erro ao carregar detalhes da venda')
    }
  }

  // Carregar histórico de autorização de um pagamento
  const loadAuthorizationHistory = async (paymentId) => {
    if (loadingHistory[paymentId] || authorizationHistory[paymentId]) {
      return // Já carregando ou já carregado
    }

    try {
      setLoadingHistory(prev => ({ ...prev, [paymentId]: true }))
      const response = await salePaymentService.getAuthorizationHistory(paymentId)
      
      if (response.success) {
        setAuthorizationHistory(prev => ({
          ...prev,
          [paymentId]: response.data || []
        }))
      }
    } catch (error) {
      console.error(`Erro ao carregar histórico do pagamento ${paymentId}:`, error)
    } finally {
      setLoadingHistory(prev => ({ ...prev, [paymentId]: false }))
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const formatDate = (date) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('pt-BR')
  }

  const getStatusBadge = (status) => {
    const badges = {
      draft: { label: 'Rascunho', class: 'badge-draft' },
      confirmed: { label: 'Confirmada', class: 'badge-confirmed' },
      cancelled: { label: 'Cancelada', class: 'badge-cancelled' },
      completed: { label: 'Concluída', class: 'badge-completed' },
      waiting: { label: 'Aguardando', class: 'badge-waiting' }
    }
    const badge = badges[status] || { label: status, class: 'badge-default' }
    return <span className={`status-badge ${badge.class}`}>{badge.label}</span>
  }

  // Copiar número de autorização para área de transferência
  const handleCopyAuthorizationNumber = async (authNumber, paymentId) => {
    try {
      await navigator.clipboard.writeText(authNumber)
      setCopiedAuthNumber(paymentId)
      setTimeout(() => setCopiedAuthNumber(null), 2000) // Reset após 2 segundos
    } catch (err) {
      console.error('Erro ao copiar:', err)
      alert('Erro ao copiar número de autorização')
    }
  }

  // Obter URL do gateway para buscar transação
  const getGatewayUrl = (payment) => {
    if (payment.pagseguro_transaction_id) {
      // URL do PagSeguro/PagBank para consultar transação
      // Formato: https://pagseguro.uol.com.br/transaction/details.jhtml?code=TRANSACTION_ID
      // Ou usar a API do PagBank se tiver acesso
      const transactionId = payment.pagseguro_transaction_id
      // Se for um ID de order (ORDE_...), usar URL de order
      if (transactionId.startsWith('ORDE_')) {
        return `https://pagseguro.uol.com.br/transaction/details.jhtml?code=${transactionId}`
      }
      // Se for um ID de charge (CHAR_...), usar URL de charge
      if (transactionId.startsWith('CHAR_')) {
        return `https://pagseguro.uol.com.br/transaction/details.jhtml?code=${transactionId}`
      }
      // Fallback genérico
      return `https://pagseguro.uol.com.br/transaction/details.jhtml?code=${transactionId}`
    }
    return null
  }

  // Exportar relatório para Excel/CSV
  const handleExportReport = async (format = 'xlsx') => {
    try {
      setLoading(true)
      
      // Buscar todas as vendas (sem paginação) com os filtros aplicados
      const allVendas = await loadAllVendasForExport()
      
      if (!allVendas || allVendas.length === 0) {
        alert('Nenhuma venda encontrada para exportar')
        setLoading(false)
        return
      }

      // Buscar detalhes completos de cada venda (com pagamentos)
      const vendasComDetalhes = await Promise.all(
        allVendas.map(async (venda) => {
          try {
            const response = await saleService.getById(venda.id)
            if (response.success) {
              return response.data
            }
            return venda
          } catch (error) {
            console.error(`Erro ao buscar detalhes da venda ${venda.id}:`, error)
            return venda
          }
        })
      )

      // Preparar dados para exportação
      const exportData = vendasComDetalhes.map((venda) => {
        // Obter números de autorização dos pagamentos
        const authorizationNumbers = venda.payments
          ?.filter(p => p.authorization_number)
          .map(p => p.authorization_number)
          .join('; ') || '-'

        // Obter formas de pagamento
        const paymentMethods = venda.payments
          ?.map(p => p.payment_method_name || 'Não informado')
          .join('; ') || '-'

        return {
          'Número': venda.number || '-',
          'Cliente': venda.customer_name || 'Cliente não informado',
          'Data': formatDate(venda.sale_date),
          'Status': getStatusLabel(venda.status),
          'Valor Total': venda.final_amount || 0,
          'Formas de Pagamento': paymentMethods,
          'Nº Autorização': authorizationNumbers,
          'Observações': venda.observation || '-'
        }
      })

      // Criar workbook
      const worksheet = XLSX.utils.json_to_sheet(exportData)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Vendas')

      // Gerar nome do arquivo com data/hora
      const now = new Date()
      const dateStr = now.toISOString().split('T')[0]
      const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-')
      const filename = `relatorio_vendas_${dateStr}_${timeStr}.${format === 'csv' ? 'csv' : 'xlsx'}`

      // Exportar
      if (format === 'csv') {
        XLSX.writeFile(workbook, filename, { bookType: 'csv' })
      } else {
        XLSX.writeFile(workbook, filename, { bookType: 'xlsx' })
      }

      alert(`Relatório exportado com sucesso! ${allVendas.length} venda(s) exportada(s).`)
    } catch (error) {
      console.error('Erro ao exportar relatório:', error)
      alert('Erro ao exportar relatório. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const handleCloseDanfeModal = () => {
    setDanfeModalOpen(false)
    setDanfeUrl(null)
    setDanfeTitle(null)
  }

  const handleCloseNfceModal = () => {
    setNfceModalOpen(false)
    setVendaParaVisualizarNfce(null)
  }

  const openDanfeUrl = (url, title) => {
    if (!url) {
      showToast('URL do documento não disponível', 'error')
      return
    }
    setDanfeTitle(title)
    setDanfeUrl(url)
    setDanfeModalOpen(true)
  }

  const handleVisualizarNotaNfe = async (venda) => {
    if (!venda.nfe_id) return
    try {
      const response = await nfeService.visualizarDANFE(venda.nfe_id)
      if (response?.success && response.url) {
        openDanfeUrl(response.url, `NF-e #${venda.nfe_number || venda.nfe_id}`)
      } else if (venda.nfe_url_pdf) {
        window.open(venda.nfe_url_pdf, '_blank')
      } else {
        showToast('DANFE não disponível', 'error')
      }
    } catch (error) {
      if (venda.nfe_url_pdf) {
        window.open(venda.nfe_url_pdf, '_blank')
      } else {
        showToast(error.response?.data?.message || 'Erro ao visualizar DANFE', 'error')
      }
    }
  }

  const handleVisualizarNfce = (venda) => {
    if (!venda.chave_nfe && !venda.referencia_nfe) {
      showToast('NFC-e não encontrada para esta venda.', 'warning')
      return
    }
    setVendaParaVisualizarNfce(venda)
    setNfceModalOpen(true)
  }

  const handleVisualizarCupom = async (venda) => {
    setCupomModalOpen(true)
    setCupomLoading(true)
    setCupomTexto('')
    try {
      const response = await fiscalService.imprimirCupomSimples(venda.id, '80mm')
      if (response.success && response.data?.texto) {
        setCupomTexto(response.data.texto)
      } else {
        setCupomTexto(response.message || 'Não foi possível gerar o cupom.')
      }
    } catch (error) {
      setCupomTexto(error.response?.data?.message || 'Erro ao carregar cupom simples.')
    } finally {
      setCupomLoading(false)
    }
  }

  const handleDownloadCupomTexto = () => {
    if (!cupomTexto || cupomLoading) return
    const blob = new Blob([cupomTexto], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `cupom-simples-${Date.now()}.txt`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const handleDownloadCupomVenda = async (venda) => {
    try {
      const response = await fiscalService.imprimirCupomSimples(venda.id, '80mm')
      const texto = response.data?.texto
      if (!response.success || !texto) {
        showToast(response.message || 'Não foi possível gerar o cupom.', 'warning')
        return
      }
      const blob = new Blob([texto], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `cupom-simples-${venda.number || venda.id}.txt`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      showToast('Cupom baixado', 'success')
    } catch (error) {
      showToast(error.response?.data?.message || 'Erro ao baixar cupom simples.', 'error')
    }
  }

  const handleConsultarStatus = async (venda, doc) => {
    try {
      if (doc.tipo === VENDA_DOC_TIPO.NFE && venda.nfe_id) {
        const response = await nfeService.consultarStatus(venda.nfe_id)
        if (response.success) {
          showToast('Status da NF-e atualizado', 'success')
          loadVendas()
        } else {
          showToast(response.message || 'Erro ao consultar NF-e', 'error')
        }
      } else if (doc.tipo === VENDA_DOC_TIPO.NFCE) {
        const response = await fiscalService.consultarStatus(venda.id)
        if (response.success) {
          showToast('Status da NFC-e atualizado', 'success')
          loadVendas()
        } else {
          showToast(response.message || 'Erro ao consultar NFC-e', 'error')
        }
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Erro ao consultar status', 'error')
    }
  }

  const handleConfirmCancelNfe = async (justificativa) => {
    if (!selectedVendaFiscal?.nfe_id) return
    setCancellingNfe(true)
    try {
      const response = await nfeService.cancelar(selectedVendaFiscal.nfe_id, justificativa)
      if (response.success) {
        showToast('NF-e cancelada com sucesso', 'success')
        setCancelNfeModalOpen(false)
        setSelectedVendaFiscal(null)
        loadVendas()
      } else {
        showToast(response.message || 'Erro ao cancelar NF-e', 'error')
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Erro ao cancelar NF-e', 'error')
    } finally {
      setCancellingNfe(false)
    }
  }

  const handleConfirmCancelNfce = async (formData) => {
    if (!selectedVendaFiscal?.id) return
    try {
      const response = await fiscalService.cancelarNFCe(
        selectedVendaFiscal.id,
        formData.justificativa,
        formData.usuario,
        formData.senha
      )
      if (response.success) {
        showToast('NFC-e cancelada com sucesso', 'success')
        setCancelNfceModalOpen(false)
        setSelectedVendaFiscal(null)
        loadVendas()
      } else {
        throw new Error(response.message || 'Erro ao cancelar NFC-e')
      }
    } catch (error) {
      throw error
    }
  }

  const handleCancelarVenda = (venda) => {
    setConfirmModal({
      isOpen: true,
      title: 'Cancelar venda',
      message: `Deseja cancelar a venda #${venda.number}? O estoque será estornado quando aplicável.`,
      onConfirm: async () => {
        try {
          const response = await saleService.cancel(venda.id)
          if (response.success) {
            showToast('Venda cancelada com sucesso', 'success')
            loadVendas()
          } else {
            showToast(response.message || 'Erro ao cancelar venda', 'error')
          }
        } catch (error) {
          showToast(error.response?.data?.message || 'Erro ao cancelar venda', 'error')
        }
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })
      }
    })
  }

  const handleVendaFiscalAction = (action, venda, doc) => {
    switch (action) {
      case 'view':
        handleViewDetails(venda)
        break
      case 'consultar-status':
        handleConsultarStatus(venda, doc)
        break
      case 'visualizar-danfe':
        handleVisualizarNotaNfe(venda)
        break
      case 'visualizar-nfce':
        handleVisualizarNfce(venda)
        break
      case 'visualizar-cupom':
        handleVisualizarCupom(venda)
        break
      case 'download-cupom':
        handleDownloadCupomVenda(venda)
        break
      case 'cancelar-nfe':
        setSelectedVendaFiscal(venda)
        setCancelNfeInitialJustification('')
        setCancelNfeModalOpen(true)
        break
      case 'corrigir-razao-social':
        setSelectedVendaFiscal(venda)
        setCorrigirRazaoModalOpen(true)
        break
      case 'cancelar-nfce':
        setSelectedVendaFiscal(venda)
        setCancelNfceModalOpen(true)
        break
      case 'cancelar-venda':
        handleCancelarVenda(venda)
        break
      case 'emitir-nota-fiscal':
        setVendaParaEmitirNota(venda)
        setEmitirNotaModalOpen(true)
        break
      case 'emitir-devolucao':
        setVendaParaDevolucao(venda)
        setEmitirDevolucaoModalOpen(true)
        break
      case 'retry-devolucao-fiscal':
        handleRetryDevolucaoFiscal(venda)
        break
      default:
        break
    }
  }

  const handleEmitirNotaSuccess = async () => {
    showToast('Nota fiscal emitida com sucesso!', 'success')
    setEmitirNotaModalOpen(false)
    setVendaParaEmitirNota(null)
    await loadVendas()
  }

  const handleEmitirDevolucaoSuccess = async (result) => {
    if (result?.fiscalStatus === 'authorized' || result?.success) {
      showToast('Devolução processada com sucesso!', 'success')
    } else {
      showToast(
        'Estoque atualizado. Reenvie a NF de devolução pelo menu da venda.',
        'warning'
      )
    }
    setEmitirDevolucaoModalOpen(false)
    setVendaParaDevolucao(null)
    await loadVendas()
  }

  const handleRetryDevolucaoFiscal = async (venda) => {
    try {
      const res = await saleReturnService.retryFiscal(venda.id)
      if (res.success && res.data?.success !== false) {
        showToast('NF de devolução autorizada!', 'success')
      } else {
        showToast(res.message || 'Falha ao reenviar NF de devolução', 'error')
      }
      await loadVendas()
    } catch (error) {
      showToast(error.response?.data?.message || 'Erro ao reenviar NF de devolução', 'error')
    }
  }

  const getDocumentoBadge = (venda) => {
    const devolucaoBadge = getDevolucaoBadge(venda)
    const doc = getVendaDocumentoFiscal(venda)
    const label = getDocumentoFiscalLabel(doc.tipo)
    const classMap = {
      [VENDA_DOC_TIPO.NFE]: { bg: '#dbeafe', color: '#1e40af' },
      [VENDA_DOC_TIPO.NFCE]: { bg: '#f3e8ff', color: '#6b21a8' },
      [VENDA_DOC_TIPO.CUPOM]: { bg: '#f3f4f6', color: '#4b5563' }
    }
    const style = classMap[doc.tipo] || classMap[VENDA_DOC_TIPO.CUPOM]
    return (
      <span style={{ display: 'inline-flex', gap: '0.35rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <span
          style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            padding: '0.2rem 0.5rem',
            borderRadius: '4px',
            background: style.bg,
            color: style.color
          }}
        >
          {label}
        </span>
        {devolucaoBadge && (
          <span
            style={{
              fontSize: '0.7rem',
              fontWeight: 600,
              padding: '0.15rem 0.45rem',
              borderRadius: '4px',
              background: devolucaoBadge.bg,
              color: devolucaoBadge.color
            }}
          >
            {devolucaoBadge.label}
          </span>
        )}
      </span>
    )
  }

  // Obter label do status para exportação
  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Rascunho',
      confirmed: 'Confirmada',
      cancelled: 'Cancelada',
      completed: 'Concluída',
      waiting: 'Aguardando'
    }
    return labels[status] || status
  }

  const columns = [
    {
      key: 'number',
      label: 'Número',
      sortable: true,
      render: (value) => <strong>{value}</strong>
    },
    {
      key: 'customer_name',
      label: 'Cliente',
      sortable: true,
      render: (value, row) => {
        if (value && value !== 'Consumidor Final') return value
        if (row?.customer_cpf_cnpj) {
          const digits = String(row.customer_cpf_cnpj).replace(/\D/g, '')
          if (digits.length === 11) return formatCPF(digits)
          return row.customer_cpf_cnpj
        }
        if (value === 'Consumidor Final') return value
        return 'Cliente não informado'
      }
    },
    {
      key: 'sale_date',
      label: 'Data',
      sortable: true,
      render: (value) => formatDate(value)
    },
    {
      key: 'final_amount',
      label: 'Total',
      sortable: true,
      className: 'erp-table-num',
      render: (value) => formatCurrency(value)
    },
    {
      key: 'payment_methods',
      label: 'Pagamento',
      render: (value) => value || '-'
    },
    {
      key: 'documento',
      label: 'Documento',
      render: (_, row) => getDocumentoBadge(row)
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {getStatusBadge(value)}
          {value === 'cancelled' && extornosPendentes[row.id]?.tem_extornos_pendentes && (
            <span
              className="relacao-vendas-extorno-warning"
              title={`${extornosPendentes[row.id].extornos_pendentes} extorno(s) pendente(s) - Estoque não foi estornado corretamente`}
            >
              Estoque não estornado
            </span>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      label: '',
      align: 'center',
      headerClassName: 'erp-table-actions-col',
      className: 'erp-table-actions-col',
      render: (_, row) => {
        const rowKey = String(row.id)
        const isOpen = openDropdownId != null && String(openDropdownId) === rowKey

        const handleDropdownToggle = (e) => {
          e.preventDefault()
          e.stopPropagation()
          if (isOpen) {
            setOpenDropdownId(null)
            setMenuVenda(null)
            menuAnchorRef.current = null
          } else {
            menuAnchorRef.current = e.currentTarget
            setMenuVenda(row)
            setOpenDropdownId(row.id)
          }
        }

        return (
          <div className="erp-table-actions-cell relacao-vendas-actions">
            <button
              className="erp-table-row-menu-btn"
              onClick={() => handleViewDetails(row)}
              title="Visualizar detalhes"
              type="button"
            >
              <Eye size={16} />
            </button>
            <div className={`dropdown-wrapper ${isOpen ? 'is-open' : ''}`}>
              <button
                type="button"
                className={`erp-table-row-menu-btn ${isOpen ? 'active' : ''}`}
                onClick={handleDropdownToggle}
                title="Mais ações"
              >
                <MoreVertical size={16} />
              </button>
            </div>
          </div>
        )
      }
    }
  ]

  const sidebarStatus = draftFilters?.status ?? ''
  const sidebarCustomerId = draftFilters?.customerId ?? ''
  const sidebarAuthorizationNumber = draftFilters?.authorizationNumber ?? ''

  return (
    <div className="relacao-vendas-page">
      <div className="page-header">
        <div>
          <h1>Relação de Vendas</h1>
          <p>Gerencie as vendas do sistema</p>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={() => navigate('/erp/vendas/vendas')}
        >
          Nova Venda
        </button>
      </div>

      <div className="relacao-vendas-toolbar">
        <div className="relacao-vendas-search-wrapper">
          <Search className="relacao-vendas-search-icon" size={18} />
          <input
            type="text"
            placeholder="Pesquisar por número ou cliente..."
            className="relacao-vendas-search-input"
            value={filters.search}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, search: e.target.value }))
              setPagination((prev) => ({ ...prev, page: 1 }))
            }}
          />
        </div>
        <div className="relacao-vendas-table-actions">
          <button
            type="button"
            onClick={openFilterSidebar}
            className={`relacao-vendas-filter-button ${hasActiveFilters ? 'active' : ''}`}
          >
            <Filter size={14} /> Filtros
            {hasActiveFilters && <span className="relacao-vendas-filter-badge" />}
          </button>
          <button
            ref={periodBtnRef}
            type="button"
            onClick={() => setIsPeriodOpen((prev) => !prev)}
            className={`relacao-vendas-filter-button ${hasDateRangeFilter ? 'active' : ''}`}
            title="Filtrar por período da venda"
          >
            <Calendar size={14} /> Período
            {hasDateRangeFilter && <span className="relacao-vendas-filter-badge" />}
          </button>
          <CalendarRangePicker
            open={isPeriodOpen}
            onOpenChange={setIsPeriodOpen}
            anchorRef={periodBtnRef}
            startValue={filters.dateFrom}
            endValue={filters.dateTo}
            onRangeChange={handlePeriodRangeChange}
            onClear={handleClearPeriodFilter}
            title="Data da venda"
            subtitle="Início e Fim"
          />
          <div
            className={`relacao-vendas-payment-picker ${filters.paymentReceiptMethodId ? 'has-value' : ''}`}
            title="Filtrar por método de pagamento"
          >
            <CreditCard size={14} className="relacao-vendas-payment-icon" aria-hidden />
            <FilterLeftPicker
              title="Método de pagamento"
              value={filters.paymentReceiptMethodId}
              options={paymentMethodOptions}
              onChange={handlePaymentMethodChange}
              emptyOptionLabel="Pagamento"
              searchable
              searchPlaceholder="Buscar método..."
            />
          </div>
          <button
            type="button"
            className="relacao-vendas-export-button"
            onClick={() => handleExportReport('xlsx')}
            disabled={loading}
            title="Exportar relatório em Excel"
          >
            <Download size={14} /> Excel
          </button>
          <button
            type="button"
            className="relacao-vendas-export-button"
            onClick={() => handleExportReport('csv')}
            disabled={loading}
            title="Exportar relatório em CSV"
          >
            <Download size={14} /> CSV
          </button>
        </div>
      </div>

      <ERPTable
        columns={columns}
        data={vendas}
        loading={loading}
        emptyMessage="Nenhuma venda encontrada"
        loadingMessage="Carregando vendas..."
        pagination={{
          page: pagination.page,
          totalPages: pagination.totalPages,
          total: pagination.total,
          onPageChange: (page) => setPagination((prev) => ({ ...prev, page })),
          showTotalCount: true
        }}
      />

      {isFilterOpen && draftFilters && (
        <>
          <div className="relacao-vendas-filter-overlay" onClick={() => setIsFilterOpen(false)} />
          <aside className="relacao-vendas-filter-sidebar relacao-vendas-scrollbar">
            <div className="relacao-vendas-filter-header">
              <div className="relacao-vendas-filter-header-content">
                <div className="relacao-vendas-filter-header-icon">
                  <Filter size={24} />
                </div>
                <div>
                  <h3 className="relacao-vendas-filter-header-title">Filtros</h3>
                  <p className="relacao-vendas-filter-header-subtitle">Refinar resultados</p>
                </div>
              </div>
              <button
                type="button"
                className="relacao-vendas-filter-close"
                onClick={() => setIsFilterOpen(false)}
                aria-label="Fechar filtros"
              >
                <X size={20} />
              </button>
            </div>

            <div className="relacao-vendas-filter-content">
              <div className="relacao-vendas-filter-section">
                <h4 className="relacao-vendas-filter-section-title">Status</h4>
                <div className="relacao-vendas-filter-options">
                  <button
                    type="button"
                    onClick={() => setDraftFilters((prev) => ({ ...prev, status: '' }))}
                    className={`relacao-vendas-filter-option ${sidebarStatus === '' ? 'selected' : ''}`}
                  >
                    <div className="relacao-vendas-filter-option-content">
                      <Filter size={18} className="relacao-vendas-filter-option-icon" />
                      <span className="relacao-vendas-filter-option-label">Todos os status</span>
                    </div>
                    {sidebarStatus === '' && (
                      <Check size={16} className="relacao-vendas-filter-option-check" />
                    )}
                  </button>
                  {STATUS_OPTIONS.map((opt) => {
                    const isSelected = sidebarStatus === opt.id
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setDraftFilters((prev) => ({ ...prev, status: opt.id }))}
                        className={`relacao-vendas-filter-option ${isSelected ? 'selected' : ''}`}
                      >
                        <div className="relacao-vendas-filter-option-content">
                          <opt.icon size={18} className="relacao-vendas-filter-option-icon" />
                          <span className="relacao-vendas-filter-option-label">{opt.label}</span>
                        </div>
                        {isSelected && (
                          <Check size={16} className="relacao-vendas-filter-option-check" />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="relacao-vendas-filter-section">
                <FilterLeftPicker
                  label="Cliente"
                  title="Cliente"
                  value={sidebarCustomerId}
                  options={customerFilterOptions}
                  onChange={(value) =>
                    setDraftFilters((prev) => ({ ...prev, customerId: value || '' }))
                  }
                  emptyOptionLabel="Todos os clientes"
                  searchable
                  searchPlaceholder="Buscar cliente..."
                  openOnHover
                />
              </div>

              <div className="relacao-vendas-filter-section">
                <h4 className="relacao-vendas-filter-section-title">Nº Autorização</h4>
                <div className="relacao-vendas-filter-auth-wrapper">
                  <Search className="relacao-vendas-filter-auth-icon" size={16} />
                  <input
                    type="text"
                    className="relacao-vendas-filter-auth-input"
                    placeholder="Buscar por autorização..."
                    value={sidebarAuthorizationNumber}
                    onChange={(e) =>
                      setDraftFilters((prev) => ({
                        ...prev,
                        authorizationNumber: e.target.value
                      }))
                    }
                  />
                </div>
              </div>
            </div>

            <div className="relacao-vendas-filter-footer">
              <button type="button" className="relacao-vendas-filter-reset" onClick={resetFilters}>
                Limpar Filtros
              </button>
              <button
                type="button"
                className="relacao-vendas-filter-apply"
                onClick={applySidebarFilters}
              >
                Aplicar Filtros
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Modal de Detalhes da Venda */}
      <Modal
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        title={`Venda #${selectedVenda?.number || ''}`}
        size="large"
      >
        {selectedVenda && (
          <div className="pre-venda-details">
            <div className="details-section">
              <h3>Informações Gerais</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <label>Número:</label>
                  <span>{selectedVenda.number}</span>
                </div>
                <div className="detail-item">
                  <label>Cliente:</label>
                  <span>{selectedVenda.customer_name || 'Cliente não informado'}</span>
                </div>
                <div className="detail-item">
                  <label>Data:</label>
                  <span>{formatDate(selectedVenda.sale_date)}</span>
                </div>
                <div className="detail-item">
                  <label>Status:</label>
                  {getStatusBadge(selectedVenda.status)}
                </div>
                <div className="detail-item">
                  <label>Total:</label>
                  <span className="total-amount">{formatCurrency(selectedVenda.final_amount)}</span>
                </div>
              </div>
            </div>

            {selectedVenda.observation && (
              <div className="details-section">
                <h3>Observações</h3>
                <p>{selectedVenda.observation}</p>
              </div>
            )}

            {selectedVenda.items && selectedVenda.items.length > 0 && (
              <div className="details-section">
                <h3>Itens</h3>
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Quantidade</th>
                      <th>Preço Unit.</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedVenda.items.map((item, index) => (
                      <tr key={index}>
                        <td>{item.product_name || `Produto #${item.product_id}`}</td>
                        <td>{item.quantity}</td>
                        <td>{formatCurrency(item.unit_price)}</td>
                        <td>{formatCurrency(item.total_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {selectedVenda.payments && selectedVenda.payments.length > 0 && (
              <div className="details-section">
                <h3>Pagamentos</h3>
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Forma de Pagamento</th>
                      <th>Valor</th>
                      <th>Status</th>
                      <th>Nº Autorização</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedVenda.payments.map((payment, index) => {
                      const gatewayUrl = getGatewayUrl(payment)
                      const hasAuthNumber = !!payment.authorization_number
                      const isCopied = copiedAuthNumber === payment.id
                      
                      return (
                        <tr key={index}>
                          <td>{payment.payment_method_name || 'Não informado'}</td>
                          <td>{formatCurrency(payment.amount)}</td>
                          <td>
                            <span className={`status-badge ${payment.status === 'paid' ? 'badge-completed' : 'badge-waiting'}`}>
                              {payment.status === 'paid' ? 'Pago' : 'Pendente'}
                            </span>
                          </td>
                        <td>
                          {hasAuthNumber ? (
                            <div className="authorization-number-cell">
                              <span className="authorization-number" style={{ fontFamily: 'monospace', fontWeight: '600' }}>
                                {payment.authorization_number}
                              </span>
                              {authorizationHistory[payment.id] && authorizationHistory[payment.id].length > 0 && (
                                <span 
                                  className="history-badge" 
                                  title={`${authorizationHistory[payment.id].length} alteração(ões) registrada(s)`}
                                  style={{
                                    fontSize: '0.75rem',
                                    color: '#059669',
                                    marginLeft: '0.5rem',
                                    cursor: 'help'
                                  }}
                                >
                                  ({authorizationHistory[payment.id].length})
                                </span>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>-</span>
                          )}
                        </td>
                          <td>
                            <div className="payment-actions">
                              {hasAuthNumber && (
                                <button
                                  className="action-btn copy-btn"
                                  onClick={() => handleCopyAuthorizationNumber(payment.authorization_number, payment.id)}
                                  title="Copiar número de autorização"
                                >
                                  {isCopied ? (
                                    <>
                                      <Check size={14} />
                                      <span>Copiado!</span>
                                    </>
                                  ) : (
                                    <>
                                      <Copy size={14} />
                                      <span>Copiar</span>
                                    </>
                                  )}
                                </button>
                              )}
                              {gatewayUrl && (
                                <a
                                  href={gatewayUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="action-btn link-btn"
                                  title="Ver transação no gateway"
                                >
                                  <ExternalLink size={14} />
                                  <span>Ver no Gateway</span>
                                </a>
                              )}
                              {hasAuthNumber && (
                                <button
                                  className="action-btn history-btn"
                                  onClick={() => {
                                    setSelectedPaymentForHistory(payment)
                                    loadAuthorizationHistory(payment.id).then(() => {
                                      setHistoryModalOpen(true)
                                    })
                                  }}
                                  title="Ver histórico de alterações"
                                >
                                  <History size={14} />
                                  <span>Histórico</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal de Histórico de Autorização */}
      <Modal
        isOpen={historyModalOpen}
        onClose={() => {
          setHistoryModalOpen(false)
          setSelectedPaymentForHistory(null)
        }}
        title={`Histórico de Autorização - Pagamento #${selectedPaymentForHistory?.id || ''}`}
        size="large"
      >
        {selectedPaymentForHistory && (
          <div className="authorization-history-modal">
            <div className="history-info">
              <div className="info-item">
                <label>Número de Autorização Atual:</label>
                <span className="authorization-number" style={{ fontFamily: 'monospace', fontWeight: '600' }}>
                  {selectedPaymentForHistory.authorization_number || '-'}
                </span>
              </div>
              <div className="info-item">
                <label>Forma de Pagamento:</label>
                <span>{selectedPaymentForHistory.payment_method_name || 'Não informado'}</span>
              </div>
            </div>

            {loadingHistory[selectedPaymentForHistory.id] ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                Carregando histórico...
              </div>
            ) : authorizationHistory[selectedPaymentForHistory.id]?.length > 0 ? (
              <div className="history-list">
                <h3>Alterações Registradas</h3>
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Data/Hora</th>
                      <th>Número Anterior</th>
                      <th>Novo Número</th>
                      <th>Alterado Por</th>
                      <th>Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {authorizationHistory[selectedPaymentForHistory.id].map((record, index) => (
                      <tr key={index}>
                        <td>{formatDate(record.created_at)} {new Date(record.created_at).toLocaleTimeString('pt-BR')}</td>
                        <td>
                          {record.old_authorization_number ? (
                            <span className="authorization-number" style={{ fontFamily: 'monospace' }}>
                              {record.old_authorization_number}
                            </span>
                          ) : (
                            <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Primeira atribuição</span>
                          )}
                        </td>
                        <td>
                          {record.new_authorization_number ? (
                            <span className="authorization-number" style={{ fontFamily: 'monospace', fontWeight: '600' }}>
                              {record.new_authorization_number}
                            </span>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>-</span>
                          )}
                        </td>
                        <td>
                          {record.changed_by_name ? (
                            <div>
                              <div>{record.changed_by_name}</div>
                              {record.changed_by_email && (
                                <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                                  {record.changed_by_email}
                                </small>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Sistema/Webhook</span>
                          )}
                        </td>
                        <td>
                          {record.change_reason || '-'}
                          {record.ip_address && (
                            <small style={{ display: 'block', color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                              IP: {record.ip_address}
                            </small>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                <History size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                <p>Nenhuma alteração registrada para este número de autorização.</p>
                <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  O histórico será registrado automaticamente quando o número for alterado.
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>

      <NFeCancelModal
        isOpen={cancelNfeModalOpen}
        onClose={() => {
          setCancelNfeModalOpen(false)
          setSelectedVendaFiscal(null)
        }}
        onConfirm={handleConfirmCancelNfe}
        nfe={
          selectedVendaFiscal
            ? {
                id: selectedVendaFiscal.nfe_id,
                number: selectedVendaFiscal.nfe_number,
                access_key: selectedVendaFiscal.nfe_access_key
              }
            : null
        }
        loading={cancellingNfe}
        initialJustificativa={cancelNfeInitialJustification}
      />

      <NFeCorrigirRazaoSocialModal
        isOpen={corrigirRazaoModalOpen}
        onClose={() => setCorrigirRazaoModalOpen(false)}
        onConfirm={async (name) => {
          if (!selectedVendaFiscal?.nfe_id) return false
          setSavingRazaoSocial(true)
          try {
            const response = await nfeService.corrigirRazaoSocial(selectedVendaFiscal.nfe_id, name)
            if (response.success) {
              showToast(response.message || 'Razão social atualizada.', 'success')
              setSelectedVendaFiscal((current) => current ? { ...current, customer_name: name } : current)
              loadVendas()
              return true
            }
            showToast(response.message || 'Erro ao corrigir a razão social', 'error')
            return false
          } catch (error) {
            showToast(error.response?.data?.message || 'Erro ao corrigir a razão social', 'error')
            return false
          } finally {
            setSavingRazaoSocial(false)
          }
        }}
        onRequestCancel={() => {
          setCorrigirRazaoModalOpen(false)
          setCancelNfeInitialJustification(RAZAO_SOCIAL_CANCEL_JUSTIFICATION)
          setCancelNfeModalOpen(true)
        }}
        nfe={
          selectedVendaFiscal
            ? {
                id: selectedVendaFiscal.nfe_id,
                number: selectedVendaFiscal.nfe_number,
                customer_name: selectedVendaFiscal.customer_name,
                customer_id: selectedVendaFiscal.customer_id
              }
            : null
        }
        loading={savingRazaoSocial}
      />

      <ModalAutorizacaoCancelamento
        isOpen={cancelNfceModalOpen}
        onClose={() => {
          setCancelNfceModalOpen(false)
          setSelectedVendaFiscal(null)
        }}
        onConfirm={handleConfirmCancelNfce}
        venda={selectedVendaFiscal}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
      />

      <DanfeViewerModal
        isOpen={danfeModalOpen}
        onClose={handleCloseDanfeModal}
        url={danfeUrl}
        title={danfeTitle || 'DANFE — NF-e'}
      />

      <NfceViewerModal
        isOpen={nfceModalOpen}
        onClose={handleCloseNfceModal}
        saleId={vendaParaVisualizarNfce?.id}
        saleNumber={vendaParaVisualizarNfce?.number}
      />

      <ModalERP
        isOpen={cupomModalOpen}
        onClose={() => {
          setCupomModalOpen(false)
          setCupomTexto('')
        }}
        title="Cupom simples (não fiscal)"
        subtitle="Visualização do cupom não fiscal"
        icon={<FileText size={22} />}
        size="md"
        footer={
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="modal-erp-btn-primary"
              onClick={handleDownloadCupomTexto}
              disabled={cupomLoading || !cupomTexto}
              title="Baixar cupom"
            >
              <Download size={16} />
              Download
            </button>
            <button
              type="button"
              className="modal-erp-btn-secondary"
              onClick={() => {
                setCupomModalOpen(false)
                setCupomTexto('')
              }}
            >
              Fechar
            </button>
          </div>
        }
      >
        {cupomLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <Loader2 className="spin-animation" size={32} />
          </div>
        ) : (
          <pre className="relacao-vendas-cupom-texto">{cupomTexto}</pre>
        )}
      </ModalERP>

      <EmitirNotaFiscalModal
        isOpen={emitirNotaModalOpen}
        onClose={() => {
          setEmitirNotaModalOpen(false)
          setVendaParaEmitirNota(null)
        }}
        venda={vendaParaEmitirNota}
        onSuccess={handleEmitirNotaSuccess}
      />

      <EmitirDevolucaoModal
        isOpen={emitirDevolucaoModalOpen}
        onClose={() => {
          setEmitirDevolucaoModalOpen(false)
          setVendaParaDevolucao(null)
        }}
        venda={vendaParaDevolucao}
        onSuccess={handleEmitirDevolucaoSuccess}
      />

      {menuVenda && menuAnchorRef.current && openDropdownId != null && (
        <VendaFiscalDropdownMenu
          venda={menuVenda}
          onAction={handleVendaFiscalAction}
          canReturnNfe={canReturnNfe}
          onClose={() => {
            setOpenDropdownId(null)
            setMenuVenda(null)
            menuAnchorRef.current = null
          }}
          buttonRef={menuAnchorRef.current}
        />
      )}

      <Toast message={toast.message} type={toast.type} show={toast.show} onClose={hideToast} duration={4000} />
    </div>
  )
}

export default RelacaoVendasPage

